import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import "firebase/compat/database";
import "firebase/compat/firestore";
import "firebase/compat/messaging";
import "firebase/compat/analytics";
import {
  ORDERS,
  CLIENT_LAST_ORDERS,
  USERS,
  ORGANIZATIONS,
  PRODUCTS,
  DELIVERY_AREAS,
  FCM_TOKENS,
} from "../../constants/entities";

const config = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
};

// VAPID key for web push (get from Firebase Console > Project Settings > Cloud Messaging)
const VAPID_KEY = process.env.REACT_APP_VAPID_KEY;

// Check if running with emulators
// Supports: 1) env var in development, 2) localStorage flag for Cypress, 3) build-time env var
const USE_EMULATORS = (process.env.NODE_ENV === 'development' && 
                       process.env.REACT_APP_USE_EMULATORS === 'true') ||
                       (typeof window !== 'undefined' && window.localStorage?.getItem('USE_EMULATORS') === 'true') ||
                       process.env.REACT_APP_USE_EMULATORS === 'true';

class Firebase {
  constructor() {
    if (!firebase.apps.length) {
      firebase.initializeApp(config);
    }
    this.auth = firebase.auth();
    this.db = firebase.database();
    // Initialize Firestore (default database)
    this._firestore = firebase.firestore();

    this.analytics = null;
    try {
      if (typeof firebase.analytics === "function" && config.measurementId) {
        this.analytics = firebase.analytics();
      }
    } catch (error) {
      console.log("Firebase analytics not supported in this browser:", error);
    }
    
    // Connect to emulators in development
    if (USE_EMULATORS) {
      console.log('🔧 Connecting to Firebase Emulators...');
      this.db.useEmulator('localhost', 9000);
      this.auth.useEmulator('http://localhost:9099');
      this._firestore.useEmulator('localhost', 8080);
      console.log('✅ Connected to Firebase Emulators');
    }
    
    // Initialize messaging only if supported
    this.messaging = null;
    try {
      // Firebase 8.x has synchronous isSupported() for messaging
      // Check if messaging is available and supported
      if (typeof firebase.messaging === 'function') {
        const messagingFactory = firebase.messaging;
        // Try to check support if method exists, otherwise try to initialize
        const isSupported = typeof messagingFactory.isSupported === 'function' 
          ? messagingFactory.isSupported() 
          : true;
        
        if (isSupported) {
          this.messaging = firebase.messaging();
        }
      }
    } catch (error) {
      console.log('Firebase messaging not supported in this browser:', error);
    }
  }

  // *** Firestore API ***

  firestore() {
    return this._firestore;
  }

  // *** Analytics API ***

  logEvent = (name, params) => {
    if (!this.analytics) return;
    try {
      this.analytics.logEvent(name, params);
    } catch (error) {
      console.error("Analytics logEvent failed:", error);
    }
  };

  // *** FCM Messaging API ***
  
  /**
   * Request permission and get FCM token
   * @returns {Promise<string|null>} FCM token or null if denied/unsupported
   */
  async requestNotificationPermission() {
    if (!this.messaging) {
      console.log('FCM not supported in this browser');
      return null;
    }

    try {
      // Check if Notification API is available
      if (!('Notification' in window)) {
        console.log('Notification API not available');
        return null;
      }

      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        const token = await this.messaging.getToken({ vapidKey: VAPID_KEY });
        console.log('FCM Token obtained:', token);
        return token;
      } else {
        console.log('Notification permission denied');
        return null;
      }
    } catch (error) {
      console.error('Error getting FCM token:', error);
      return null;
    }
  }

  /**
   * Save FCM token to database for an organization
   * @param {string} orgId - Organization ID
   * @param {string} token - FCM token
   * @param {string} userId - User ID
   */
  async saveFcmToken(orgId, token, userId) {
    if (!token) return;
    

    const tokenData = {
      token,
      createdAt: new Date().toJSON(),
      updatedAt: new Date().toJSON(),
      userAgent: navigator.userAgent,
    };
    if (userId !== undefined) {
      tokenData.userId = userId;
    }

    // Use token as key to avoid duplicates
    const tokenKey = token.substring(0, 50).replace(/[.#$[\]]/g, '_');
    await this.db.ref(`${ORGANIZATIONS}/${orgId}/${FCM_TOKENS}/${tokenKey}`).set(tokenData);
  }

  /**
   * Remove FCM token from database
   * @param {string} orgId - Organization ID  
   * @param {string} token - FCM token
   */
  async removeFcmToken(orgId, token) {
    if (!token) return;
    const tokenKey = token.substring(0, 50).replace(/[.#$[\]]/g, '_');
    await this.db.ref(`${ORGANIZATIONS}/${orgId}/${FCM_TOKENS}/${tokenKey}`).remove();
  }

  /**
   * Listen for foreground messages
   * @param {function} callback - Callback when message received
   */
  onForegroundMessage(callback) {
    if (!this.messaging) {
      console.log('Messaging not available, skipping foreground listener');
      return null;
    }
    try {
      return this.messaging.onMessage(callback);
    } catch (error) {
      console.error('Error setting up foreground message listener:', error);
      return null;
    }
  }

  doCreateUserWithEmailAndPassword = (email, password) =>
    this.auth.createUserWithEmailAndPassword(email, password);

  doSignInWithEmailAndPassword = (email, password) =>
    this.auth.signInWithEmailAndPassword(email, password);

  doSignOut = () => this.auth.signOut();

  doPasswordReset = (email) => this.auth.sendPasswordResetEmail(email);

  doPasswordUpdate = (password) =>
    this.auth.currentUser.updatePassword(password);

  // *** User API ***
  user = (uid) => this.db.ref(`${USERS}/${uid}`);
  users = () => this.db.ref(USERS);

  organization = (uid) => this.db.ref(`${ORGANIZATIONS}/${uid}`);
  organizations = () => this.db.ref(ORGANIZATIONS);

  order = (org, uid) => this.db.ref(`${ORGANIZATIONS}/${org}/${ORDERS}/${uid}`);
  orders = (org) => this.db.ref(`${ORGANIZATIONS}/${org}/${ORDERS}`);

  product = (org, uid) =>
    this.db.ref(`${ORGANIZATIONS}/${org}/${PRODUCTS}/${uid}`);
  products = (org) => this.db.ref(`${ORGANIZATIONS}/${org}/${PRODUCTS}`);

  lastOrder = (org, uid) =>
    this.db.ref(`${ORGANIZATIONS}/${org}/${CLIENT_LAST_ORDERS}/${uid}`);
  lastOrders = (org) =>
    this.db.ref(`${ORGANIZATIONS}/${org}/${CLIENT_LAST_ORDERS}`);

  // *** Delivery Areas API ***
  deliveryArea = (org, uid) =>
    this.db.ref(`${ORGANIZATIONS}/${org}/${DELIVERY_AREAS}/${uid}`);
  deliveryAreas = (org) =>
    this.db.ref(`${ORGANIZATIONS}/${org}/${DELIVERY_AREAS}`);

  // generic ref usage
  generic = (refs, org, uid) =>
    this.db.ref(`${ORGANIZATIONS}/${org}/${refs}/${uid}`);
}

export default Firebase;
